import type { IPackChatContext } from "./types";

export const GENERAL_PAYPACK_CONTEXT = {
  heading: "PayPack",
} as const satisfies IPackChatContext;

export const isGeneralPayPackContext = (context: IPackChatContext): boolean =>
  (!context.heading || context.heading === GENERAL_PAYPACK_CONTEXT.heading) &&
  !context.line &&
  !context.amount;

/**
 * How an attached figure reads back to a human — "Monday, June 15th 2026 ·
 * Commission · $120.00".
 *
 * Shared by the technician's chip and both sides' history lists so a manager
 * reads the same words the technician saw when they tapped it. Parts the
 * statement didn't supply are dropped rather than rendered blank.
 */
export const describeReference = (context: IPackChatContext): string =>
  isGeneralPayPackContext(context)
    ? "PayPack"
    : [context.heading, context.line, context.amount]
        .filter(Boolean)
        .join(" · ");

/**
 * A figure looks like money or a quantity — "$1,700.83", "39.00", "8".
 *
 * Decides whether the value can be pulled out as a right-aligned figure or has
 * to stay in the text column. `amount` is whatever the pack put beside the
 * label, so it is just as often a pay plan name ("HVAC Installer 3% Sales") or a
 * job number as it is a dollar figure, and setting a sentence in figure type
 * wraps into three ragged lines.
 */
const FIGURE_PATTERN = /^[($-]?\$?\s?[\d,]+(\.\d+)?/;

export const isFigureValue = (value: string | undefined): boolean =>
  Boolean(value) && FIGURE_PATTERN.test(value!.trim());

export interface PackChatReferenceParts {
  /** The section the line sits in, when the line itself carries the name. */
  path?: string;
  /** What the technician tapped — the strongest part, so it leads. */
  label: string;
  /** The value beside the label, if the pack supplied one. */
  value?: string;
  /** Whether `value` can be set as a figure rather than as prose. */
  valueIsFigure: boolean;
}

/**
 * Splits an attached reference into the parts a layout needs.
 *
 * `describeReference` joins everything with dots, which is right for one line of
 * running text but wrong for a panel: the label wants to be the headline, the
 * heading wants to be the quieter path above or below it, and a dollar figure
 * wants to sit on the right in its own weight.
 */
export const referenceParts = (
  context: IPackChatContext,
): PackChatReferenceParts => {
  const label = context.line || context.heading || "PayPack";
  const path = context.line ? context.heading : undefined;

  return {
    path,
    label,
    value: context.amount,
    valueIsFigure: isFigureValue(context.amount),
  };
};
